import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Crown, Compass } from "lucide-react";

const GOLD = "#C9A84C";
const CALENDLY_URL = "https://calendly.com/r-cassilde";

const PHASES = [
  { r: "Phase 1", s: "Séances 1-3", t: "Décodage",
    d: "Triple Fusion™, calcul numérologique complet, matrice Lo Shu personnalisée." },
  { r: "Phase 2", s: "Séances 4-6", t: "Cartographie",
    d: "Territoires de vie, identification des blocages, contrat ancestral IFÀ." },
  { r: "Phase 3", s: "Séances 7-9", t: "Réalignement",
    d: "Plan d'action souverain, décrets de vie, réorganisation Lo Shu." },
  { r: "Phase 4", s: "Séances 10-12", t: "Ancrage & Mission profonde",
    d: "Ancrage définitif, mission de vie, clôture souveraine." },
];

const INCLUS = [
  "Calcul Triple Fusion™ personnalisé",
  "Les 12 Décrets Souverains",
  "Carnet de Travail Souverain",
  "Accès à l'espace membres exclusif",
];

const Reveal = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
  >{children}</motion.div>
);

export default function CoachingElite() {
  useEffect(() => {
    document.title = "Coaching Élite — Triple Fusion™ | Académie AETHERIS";
  }, []);

  return (
    <div className="min-h-screen bg-brand-ink text-white pt-24 pb-24" data-testid="coaching-elite-page">
      <div className="max-w-[1100px] mx-auto px-6 md:px-10">

        <motion.header
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 mb-6" style={{ color: GOLD }}>
            <Crown size={18} strokeWidth={1.5} />
            <p className="text-xs uppercase tracking-[0.25em]">Programme Élite</p>
          </div>
          <h1 className="font-serif text-5xl md:text-7xl leading-[0.95]">
            Coaching Élite<br />
            <span style={{ color: GOLD }}>Triple Fusion™</span>
          </h1>
          <p className="text-white/60 text-lg md:text-xl font-light mt-8 max-w-2xl mx-auto leading-relaxed">
            Du chaos vibratoire à la clarté souveraine — en 12 séances.
          </p>
        </motion.header>

        <section className="text-center mb-20" data-testid="tarif">
          <p className="text-xs uppercase tracking-[0.25em] mb-6" style={{ color: GOLD }}>Investissement</p>
          <div className="max-w-xl mx-auto p-10 border" style={{ borderColor: GOLD, background: "rgba(201,168,76,0.08)" }}>
            <div className="font-serif text-6xl mb-2" style={{ color: GOLD }} data-testid="tarif-prix">1 950 €</div>
            <p className="text-white/60 text-sm mb-2">12 séances individuelles · 3 mois</p>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" data-testid="cta-calendly"
               className="inline-flex items-center justify-center gap-3 px-10 py-5 text-xs uppercase tracking-[0.2em] font-semibold text-brand-ink transition-transform hover:scale-[1.02]"
               style={{ backgroundColor: GOLD }}>
              Réserver mon appel découverte <ArrowRight size={16} />
            </a>
            <p className="text-white/40 text-xs mt-6">Un échange de 30 minutes avec Rodrigue CASSILDÉ pour valider que ce programme est le bon pour vous.</p>
          </div>
          <p className="text-white/40 text-xs mt-8">Suivi individuel sur 12 séances</p>
        </section>

        <section className="mb-24" data-testid="phases">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] mb-4" style={{ color: GOLD }}>Le chemin</p>
            <h2 className="font-serif text-3xl md:text-5xl mb-12">Les 4 phases du programme</h2>
          </Reveal>
          <div className="space-y-4">
            {PHASES.map((p, i) => (
              <Reveal key={p.r} delay={i * 0.05}>
                <div className="p-6 md:p-8 border border-white/10 bg-brand-surface/30 grid md:grid-cols-12 gap-6 items-center">
                  <div className="md:col-span-3">
                    <p className="font-serif text-2xl" style={{ color: GOLD }}>{p.r}</p>
                    <p className="text-xs uppercase tracking-luxe text-white/40 mt-1">{p.s}</p>
                  </div>
                  <div className="md:col-span-9">
                    <h3 className="font-serif text-xl md:text-2xl mb-2">{p.t}</h3>
                    <p className="text-white/60 font-light leading-relaxed">{p.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="mb-24" data-testid="inclus">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] mb-4" style={{ color: GOLD }}>Ce qui est inclus</p>
            <div className="grid md:grid-cols-2 gap-4">
              {INCLUS.map((it) => (
                <div key={it} className="flex items-center gap-3 p-5 border border-white/10 bg-brand-surface/30">
                  <Check size={16} style={{ color: GOLD }} strokeWidth={2.5} />
                  <span className="text-white/80">{it}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </section>

        <Reveal>
          <div className="mt-16 p-8 md:p-10 border" style={{ borderColor: `${GOLD}55`, background: "rgba(0,0,0,0.4)" }}>
            <div className="flex items-center gap-3 mb-3" style={{ color: GOLD }}>
              <Compass size={16} />
              <p className="text-xs uppercase tracking-[0.2em]">Aller plus loin</p>
            </div>
            <h3 className="font-serif text-2xl md:text-3xl mb-3">Coaching Souverain</h3>
            <p className="text-white/60 font-light leading-relaxed mb-6">
              24 séances de transformation intégrale — Ifà approfondi, Cycles Lo Shu, Feng Shui personnel, Codex Souverain.
            </p>
            <Link to="/coaching-souverain" data-testid="link-souverain"
                  className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] font-semibold border px-6 py-3 transition-colors"
                  style={{ borderColor: GOLD, color: GOLD }}>
              Découvrir le Coaching Souverain <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
